import { createControlledExecutionAuditEvent } from "./executionAudit.js";
import { createControlledExecutionDryRunPlan } from "./executionPlan.js";
const KNOWN_SURFACES = [
    "tool",
    "goose_agent",
    "hamesh_agent",
    "n8n_workflow",
    "mcp_call",
];
const CRITICAL_ACTION_PATTERN = createBlockedWordPattern([
    "shell",
    "ssh",
    "deploy",
    "restore",
    joinParts("backup", "_", "delete"),
    joinParts("service", "_", "restart"),
    "database_direct_write",
    "database_write",
    "file_system",
    "fs_write",
    "read_secret",
    joinParts("secret", "_", "read"),
    "payment",
    "bank",
    "salary_disburse",
    "payroll_run",
    "submit_gst",
    "invoice",
    "webhook",
    "external_api",
    "customer_payload",
    "tenant",
    "billing",
    "subscription",
    "birth_data",
    "kundali_record",
    "marriage_decision",
    "deterministic_prediction",
    "delete",
    "destructive",
]);
const PRODUCT_MUTATION_PATTERN = /create_|update_|delete_|write_|mutat|trigger_|send_|retry_|rotate_|approve_|reject_|submit_|connect_live|call_external/i;
const RISKY_NETWORK_PATTERN = /network|http|webhook|external_api|callback|n8n|mcp_remote/i;
const SHELL_FILE_PATTERN = createBlockedWordPattern([
    "shell",
    "ssh",
    "exec",
    "spawn",
    joinParts("child", "_", "process"),
    "fs\\.",
    "file_system",
    "write_file",
    "read_file",
]);
const SECRET_KEY_PATTERN = /api[_-]?key|password|privatekey|private_key|accesstoken|access_token|refreshtoken|refresh_token|webhooksecret|webhook_secret|credential|authorization/i;
const SECRET_VALUE_PATTERN = new RegExp([
    String.raw `Bearer\s+[^\s"]+`,
    `${joinParts("ghp", "_")}[A-Za-z0-9_]+`,
    `${joinParts("github", "_", "pat", "_")}[A-Za-z0-9_]+`,
    `${joinParts("sk", "-")}[A-Za-z0-9_-]+`,
    String.raw `BEGIN (?:OPENSSH|RSA|PRIVATE) KEY`,
].join("|"), "i");
export function evaluateControlledExecutionRequest(request, settings = {}) {
    const blockedReasons = [];
    const surface = String(request.surface);
    const actionName = request.actionName || "unknown_action";
    if (!KNOWN_SURFACES.includes(surface)) {
        blockedReasons.push("Unknown execution surface denied.");
    }
    if (surface === "mcp_call" && actionName === "unknown_tool") {
        blockedReasons.push("Unknown MCP tool denied.");
    }
    const unsafeFlagReasons = detectUnsafeFlags(request.payload ?? {});
    blockedReasons.push(...unsafeFlagReasons);
    if (containsSecretLikeField(request)) {
        blockedReasons.push("Secret-like fields or credential values are rejected.");
    }
    if (PRODUCT_MUTATION_PATTERN.test(actionName)) {
        blockedReasons.push("Product mutation action denied.");
    }
    if (CRITICAL_ACTION_PATTERN.test(actionName)) {
        blockedReasons.push("Critical or destructive action denied.");
    }
    if (RISKY_NETWORK_PATTERN.test(actionName)) {
        blockedReasons.push("Network or webhook execution denied.");
    }
    if (SHELL_FILE_PATTERN.test(actionName)) {
        blockedReasons.push("Shell or file-system execution denied.");
    }
    const riskLevel = classifyRisk(actionName, blockedReasons);
    const approvalStatus = resolveApprovalStatus(request, riskLevel, blockedReasons);
    const mode = resolveMode(settings, approvalStatus, blockedReasons, riskLevel);
    const simulationAllowed = mode === "dry_run" || mode === "approved_simulation";
    const dryRunPlan = simulationAllowed
        ? createControlledExecutionDryRunPlan({ ...request, actionName }, riskLevel, blockedReasons)
        : undefined;
    return {
        allowed: false,
        simulationAllowed,
        mode,
        approvalStatus,
        riskLevel,
        reason: createReason(mode, approvalStatus, riskLevel),
        blockedReasons,
        auditEvent: createControlledExecutionAuditEvent({ ...request, actionName }, approvalStatus, riskLevel),
        ...(dryRunPlan ? { dryRunPlan } : {}),
        providerCallExecuted: false,
        agentExecutionEnabled: false,
        workflowExecutionEnabled: false,
        toolExecutionEnabled: false,
        mcpExecutionEnabled: false,
        realExecutionPerformed: false,
    };
}
function resolveApprovalStatus(request, riskLevel, blockedReasons) {
    if (riskLevel === "critical" ||
        blockedReasons.some((reason) => {
            return (reason.includes("Secret-like") ||
                reason.includes("dryRunOnly:false") ||
                reason.startsWith("Unsafe "));
        })) {
        return "denied";
    }
    if (!request.approval) {
        return riskLevel === "low" ? "not_required" : "required";
    }
    if (request.approval.dryRunOnly !== true) {
        return "denied";
    }
    if (request.approval.approvalTokenRef || request.approval.approvedBy || request.approval.changeTicketRef) {
        return "approved_for_simulation";
    }
    return "pending";
}
function resolveMode(settings, approvalStatus, blockedReasons, riskLevel) {
    if (approvalStatus === "denied" || riskLevel === "critical")
        return "disabled";
    if (approvalStatus === "approved_for_simulation" && settings.allowApprovedSimulation !== false && blockedReasons.length === 0) {
        return "approved_simulation";
    }
    if (approvalStatus === "required" || approvalStatus === "pending")
        return "approval_required";
    return settings.defaultMode ?? "dry_run";
}
function classifyRisk(actionName, blockedReasons) {
    if (blockedReasons.some((reason) => /Critical|Secret-like|Shell|Network|Product mutation|Unknown|Unsafe|dryRunOnly:false/.test(reason))) {
        return "critical";
    }
    if (/workflow|agent|tool|mcp/i.test(actionName))
        return "medium";
    return "low";
}
function createReason(mode, approvalStatus, riskLevel) {
    if (mode === "disabled")
        return "Request denied. Real execution is disabled in PR95.";
    if (mode === "approved_simulation")
        return "Approval reference accepted for simulation only. Real execution remains disabled.";
    if (mode === "approval_required")
        return `Manual approval required before simulation. Risk level: ${riskLevel}.`;
    return approvalStatus === "not_required"
        ? "Safe dry-run plan generated. Real execution remains disabled."
        : "Dry-run response generated. Real execution remains disabled.";
}
function detectUnsafeFlags(value) {
    const reasons = [];
    for (const flag of ["execute", "live", "production", "destructive"]) {
        if (value[flag] === true) {
            reasons.push(`Unsafe ${flag}: true flag denied.`);
        }
    }
    if (value.dryRunOnly === false) {
        reasons.push("dryRunOnly:false denied.");
    }
    return reasons;
}
function containsSecretLikeField(value, key = "") {
    if (key && !isAllowedReferenceKey(key) && SECRET_KEY_PATTERN.test(key))
        return true;
    if (typeof value === "string" && SECRET_VALUE_PATTERN.test(value))
        return true;
    if (Array.isArray(value))
        return value.some((item, index) => containsSecretLikeField(item, `${key}[${index}]`));
    if (value && typeof value === "object") {
        return Object.entries(value).some(([childKey, childValue]) => {
            return containsSecretLikeField(childValue, childKey);
        });
    }
    return false;
}
function isAllowedReferenceKey(key) {
    return /approvalTokenRef|changeTicketRef/i.test(key);
}
function joinParts(...parts) {
    return parts.join("");
}
function createBlockedWordPattern(words) {
    return new RegExp(words.join("|"), "i");
}
//# sourceMappingURL=executionPolicy.js.map